// Faza 3 sledzenia pozycji: dopasowanie transkrypcji STT do sekcji tekstu
// utworu. `buildLyricsSections` numeruje sekcje TAK SAMO jak
// `render/pelnyTekst.ts` numeruje `.pt-lbl[data-section-index]` (licznik
// zaczyna sie od 0 przy pierwszym naglowku sekcji rozpoznanym przez
// `UltimateGuitarParser`), zeby `onSection()` w `main.ts` mogl scrollowac do
// tego samego elementu w widoku pelnego tekstu.

import { UltimateGuitarParser, type Line, type Song } from 'chordsheetjs';
import { preNormalize } from '../chart/preNormalize.ts';

export interface LyricsSection {
  index: number;
  /** Pelny tekst (lyrics) sekcji, wszystkie wersy zlaczone spacja. */
  text: string;
}

/** Minimalna liczba slow transkrypcji, jakie musza trafic w sekcje, zeby
 * dopasowanie w ogole zostalo zglaszane - nizej wolimy milczec niz zgadywac. */
const MIN_TRAFIONYCH_SLOW = 3;

/** Zwraca `true`, gdy `item` jest ChordLyricsPair (ma pole `chords` typu
 * string) - odrzuca `Tag`/`Comment` osadzone w tej samej linii. Ten sam
 * strażnik co w `render/pelnyTekst.ts`. */
function jestParaAkordTekst(item: unknown): item is { chords: string; lyrics: string | null } {
  return typeof item === 'object' && item !== null && 'chords' in item && typeof (item as { chords: unknown }).chords === 'string';
}

/** Sklejone `lyrics` jednej linii, z pominieciem par akord-tekst bez tekstu
 * (linia wylacznie akordowa, "chord-only", nie dodaje niczego). */
function lyricsLinii(line: Line): string {
  let out = '';
  for (const item of line.items) {
    if (!jestParaAkordTekst(item)) continue;
    if (item.lyrics) out += item.lyrics;
  }
  return out;
}

/**
 * Buduje sekcje z `rawTab` (przez `preNormalize` + `UltimateGuitarParser`),
 * jedna na kazdy naglowek sekcji rozpoznany przez parser. Kazda sekcja niesie
 * caly swoj tekst (lyrics), wersy zlaczone spacja - linie wylacznie
 * akordowe (bez lyrics) nic nie dodaja. Tresc PRZED pierwszym naglowkiem
 * (jesli jakikolwiek istnieje) jest pomijana - ten sam zakres, jaki dostaje
 * numeracje w `render/pelnyTekst.ts`. Utwor bez rozpoznanych naglowkow albo
 * niesparsowalny wraca z pusta lista (STT po prostu nie dostaje sekcji do
 * dopasowania, chromagram dziala dalej sam).
 */
export function buildLyricsSections(rawTab: string): LyricsSection[] {
  const preNorm = preNormalize(rawTab);

  let song: Song;
  try {
    song = new UltimateGuitarParser().parse(preNorm.text);
  } catch {
    return [];
  }

  const sections: LyricsSection[] = [];
  let sectionIndex = -1;
  let buffer: string[] = [];

  const flush = (): void => {
    if (sectionIndex < 0) return;
    sections.push({ index: sectionIndex, text: buffer.join(' ').trim() });
  };

  for (const line of song.lines) {
    if (line.isSectionStart()) {
      flush();
      sectionIndex += 1;
      buffer = [];
      continue;
    }
    if (line.isSectionEnd()) continue;

    const tekst = lyricsLinii(line).trim();
    if (tekst !== '') buffer.push(tekst);
  }
  flush();

  return sections;
}

/** Usuwa interpunkcje i sprowadza do malych liter, do porownania slow. */
function normalizuj(text: string): string {
  return text
    .toLowerCase()
    .replace(/[.,!?;:"'„”«»()[\]{}\-–—]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slowa(text: string): string[] {
  const znormalizowany = normalizuj(text);
  return znormalizowany === '' ? [] : znormalizowany.split(' ');
}

/**
 * Dopasowuje `transcript` do najblizszej sekcji z `sections`: dla kazdej
 * sekcji liczy, ile slow transkrypcji wystepuje w jej tekscie, i wygrywa
 * sekcja z najwieksza liczba trafien. Prog `MIN_TRAFIONYCH_SLOW`: przy mniej
 * niz trzech trafionych slowach zwraca `null` (za niska pewnosc), zamiast
 * wskazywac przypadkowa sekcje.
 */
export function matchTranscript(transcript: string, sections: LyricsSection[]): number | null {
  const slowaTranskrypcji = slowa(transcript);
  if (slowaTranskrypcji.length === 0) return null;

  let najlepszy: { index: number; trafien: number } | null = null;

  for (const section of sections) {
    const slowaSekcji = new Set(slowa(section.text));
    let trafien = 0;
    for (const slowo of slowaTranskrypcji) {
      if (slowaSekcji.has(slowo)) trafien += 1;
    }
    if (najlepszy === null || trafien > najlepszy.trafien) {
      najlepszy = { index: section.index, trafien };
    }
  }

  if (najlepszy === null || najlepszy.trafien < MIN_TRAFIONYCH_SLOW) return null;
  return najlepszy.index;
}

/** Pojedyncza linia tekstu z numerem globalnym (do podswietlania w DOM). */
export interface LyricsLine {
  lineIndex: number;
  text: string;
}

/** Buduje plaska liste linii tekstu z rawTab, numerowanych globalnie
 *  (odpowiadajacych `data-line-index` w pelnyTekst.ts). */
export function buildLyricsLines(rawTab: string): LyricsLine[] {
  const preNorm = preNormalize(rawTab);
  let song: Song;
  try {
    song = new UltimateGuitarParser().parse(preNorm.text);
  } catch {
    return [];
  }

  const lines: LyricsLine[] = [];
  let lineIndex = 0;

  for (const line of song.lines) {
    if (line.isSectionStart() || line.isSectionEnd()) continue;
    if (line.isEmpty() || !line.hasRenderableItems()) {
      lineIndex++;
      continue;
    }
    const tekst = lyricsLinii(line).trim();
    if (tekst !== '') {
      lines.push({ lineIndex, text: tekst });
    }
    lineIndex++;
  }
  return lines;
}

const MIN_TRAFIONYCH_SLOW_LINIA = 2;

/** Dopasowuje transkrypcje do konkretnej LINII tekstu (nie sekcji).
 *  Zwraca `lineIndex` najlepszego trafienia lub null. */
export function matchTranscriptToLine(transcript: string, lines: LyricsLine[]): number | null {
  const slowaT = slowa(transcript);
  if (slowaT.length === 0) return null;

  let najlepszy: { lineIndex: number; trafien: number } | null = null;

  for (const line of lines) {
    const slowaL = new Set(slowa(line.text));
    let trafien = 0;
    for (const slowo of slowaT) {
      if (slowaL.has(slowo)) trafien += 1;
    }
    if (najlepszy === null || trafien > najlepszy.trafien) {
      najlepszy = { lineIndex: line.lineIndex, trafien };
    }
  }

  if (najlepszy === null || najlepszy.trafien < MIN_TRAFIONYCH_SLOW_LINIA) return null;
  return najlepszy.lineIndex;
}
